import { motion } from "framer-motion";
import { fadeUp, stagger } from "@/lib/motion";
import { CalendarDays, Tag, ArrowUpRight, Bell } from "lucide-react";

// ─────────────────────────────────────────────────────────────
//  ADD YOUR UPDATES HERE
//  Each update object:
//    title   → Headline of the update
//    desc    → Short description
//    date    → Date string  e.g. "3 July 2025"
//    tag     → Category label e.g. "Offer", "Event", "Product", "News"
//    link    → (optional) URL or internal route — leave "" to hide button
//    image   → (optional) Image path from /public folder — leave "" to hide
//    hot     → true = shows a "🔥 New" badge
// ─────────────────────────────────────────────────────────────
const UPDATES = [
  {
    title: "TallyPrime 7.1 Now Available",
    desc: "AI-powered invoice scanning, improved GST reconciliation and faster multi-company switching. Upgrade your license today.",
    date: "1 July 2025",
    tag: "Product",
    link: "/products",
    image: "",
    hot: true,
  },
  {
    title: "Tally 3-Star Certified Partner — UP East",
    desc: "Prime Technology has been recognised as a 3-Star Sales & Implementation Partner for FY 2025-26 by Tally Solutions.",
    date: "15 June 2025",
    tag: "Achievement",
    link: "",
    image: "/gallery/tally-3-star-sales-implementation-partner-certificate.jpg",
    hot: true,
  },
  {
    title: "Customer Connect Champion Award",
    desc: "We won the Customer Connect Champion Award at the Tally Partner Meet UP East FY 2025-26 ceremony.",
    date: "10 June 2025",
    tag: "Award",
    link: "/gallery",
    image: "/gallery/customer-connect-champ-award-ceremony-fy-2025-26.jpg",
    hot: false,
  },
  {
    title: "Free TallyPrime Demo — Book Now",
    desc: "Get a free, no-obligation live demo of TallyPrime for your business. Our certified team will walk you through every feature.",
    date: "1 June 2025",
    tag: "Offer",
    link: "/#contact",
    image: "",
    hot: false,
  },
];

// Tag color map
const TAG_COLORS = {
  Product:     { bg: "bg-blue-100",   text: "text-blue-700"   },
  Achievement: { bg: "bg-purple-100", text: "text-purple-700" },
  Award:       { bg: "bg-yellow-100", text: "text-yellow-700" },
  Offer:       { bg: "bg-green-100",  text: "text-green-700"  },
  Event:       { bg: "bg-orange-100", text: "text-orange-700" },
  News:        { bg: "bg-gray-100",   text: "text-gray-700"   },
};

export default function NewUpdates() {
  if (!UPDATES.length) return null;

  return (
    <section id="updates" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Section Header */}
        <motion.div
          variants={stagger(0.1, 0.08)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12"
        >
          <div>
            <motion.span variants={fadeUp} className="eyebrow">
              <span className="dot" /> Latest News
            </motion.span>
            <motion.h2
              variants={fadeUp}
              className="mt-4 font-display font-black text-4xl sm:text-5xl leading-[1.08] tracking-tight text-[#1A1A2E]"
            >
              New <span className="font-serif-accent text-[#FFA000]">Updates</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="mt-3 text-[#4A5568] max-w-lg">
              Stay up-to-date with the latest news, product launches, achievements and offers from Prime Technology.
            </motion.p>
          </div>

          {/* Bell icon badge */}
          <motion.div variants={fadeUp} className="shrink-0">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#FFF8E1] border border-[#FFA000]/30 px-4 py-2 text-sm font-semibold text-[#FFA000]">
              <Bell className="w-4 h-4" />
              {UPDATES.filter((u) => u.hot).length} New This Month
            </div>
          </motion.div>
        </motion.div>

        {/* Cards Grid */}
        <motion.div
          variants={stagger(0.06, 0.08)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {UPDATES.map((item, i) => {
            const tagStyle = TAG_COLORS[item.tag] || TAG_COLORS["News"];
            return (
              <motion.div
                key={i}
                variants={fadeUp}
                whileHover={{ y: -5 }}
                className="relative flex flex-col rounded-2xl bg-[#FAF9F6] border border-[#0D47A1]/8 overflow-hidden shadow-[0_4px_20px_rgba(13,71,161,0.05)] hover:shadow-[0_16px_40px_rgba(13,71,161,0.10)] transition-all duration-400"
              >
                {/* Hot badge */}
                {item.hot && (
                  <div className="absolute top-3 right-3 z-10 rounded-full bg-[#0D47A1] text-white text-[10px] font-bold px-2.5 py-1 uppercase tracking-wider">
                    🔥 New
                  </div>
                )}

                {/* Image (if provided) */}
                {item.image && (
                  <div className="h-40 w-full overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                )}

                {/* Content */}
                <div className="flex flex-col flex-1 p-5">
                  {/* Tag + Date */}
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${tagStyle.bg} ${tagStyle.text}`}>
                      <Tag className="w-3 h-3" />
                      {item.tag}
                    </span>
                    <span className="flex items-center gap-1 text-[11px] text-[#8896A6]">
                      <CalendarDays className="w-3 h-3" />
                      {item.date}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="font-display font-bold text-[15px] text-[#1A1A2E] leading-snug mb-2">
                    {item.title}
                  </h3>

                  {/* Description */}
                  <p className="text-[13px] text-[#4A5568] leading-relaxed flex-1">
                    {item.desc}
                  </p>

                  {/* CTA Link */}
                  {item.link && (
                    <a
                      href={item.link}
                      className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0D47A1] hover:text-[#FFA000] transition-colors"
                    >
                      Read More <ArrowUpRight className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
